import React, { useState } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Minus, Plus, CheckCircle, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const questions = [
  { id: 'como_se_sente', title: 'Como você está se sentindo hoje?', lowLabel: 'Péssimo', highLabel: 'Excelente', type: 'scale' },
  { id: 'dores_musculares', title: 'Como estão suas dores musculares?', lowLabel: 'Sem dor', highLabel: 'Dor intensa', type: 'scale' },
  { id: 'recuperacao', title: 'Como está sua recuperação?', lowLabel: 'Muito cansado', highLabel: 'Totalmente recuperado', type: 'scale' },
  { id: 'saude_mental', title: 'Como está sua saúde mental / irritabilidade?', lowLabel: 'Muito irritado', highLabel: 'Calmo e focado', type: 'scale' },
  { id: 'fc_repouso', title: 'Sua FC em repouso hoje está:', type: 'choice', options: [
    { value: 'baixa', label: 'Baixa', desc: 'Mais baixa que o normal (bem recuperado)', color: '#39FF14' },
    { value: 'alta', label: 'Alta', desc: 'Mais alta que o normal (fadiga/estresse)', color: '#FF3366' }
  ]}
];

const Wellness = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({
    como_se_sente: 5,
    dores_musculares: 5,
    recuperacao: 5,
    saude_mental: 5,
    fc_repouso: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const question = questions[currentQuestion];
  const currentValue = answers[question.id];

  const handleIncrement = () => {
    if (currentValue < 10) setAnswers({ ...answers, [question.id]: currentValue + 1 });
  };

  const handleDecrement = () => {
    if (currentValue > 1) setAnswers({ ...answers, [question.id]: currentValue - 1 });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/wellness/submit`,
        answers,
        { withCredentials: true }
      );
      setResult(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao enviar questionário');
      console.error('Wellness submit error:', error);
      setLoading(false);
    }
  };

  // Result Screen
  if (result) {
    const status = result.daily_status;
    const StatusIcon = status.level === 'verde' ? CheckCircle : status.level === 'amarelo' ? AlertTriangle : XCircle;
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/79f43b2c808bf03b49df74617e42043ce286507138776852627882c75eb6dc21.png)',
            backgroundSize: 'cover'
          }}
        ></div>

        <div className="relative z-10 w-full max-w-2xl">
          <div className="flex flex-col items-center mb-8">
            <TCSLogo size="md" showTagline={false} />
          </div>

          <div 
            className="border-2 p-10 text-center"
            style={{ 
              borderColor: status.color,
              background: `linear-gradient(180deg, ${status.color}15 0%, #121212 100%)`
            }}
            data-testid="wellness-result-card"
          >
            <div 
              className="w-24 h-24 mx-auto mb-6 border-2 flex items-center justify-center"
              style={{ borderColor: status.color, background: `${status.color}15` }}
            >
              <StatusIcon className="w-12 h-12" style={{ color: status.color }} />
            </div>

            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-2" style={{ color: status.color }}>
              Status do Dia
            </p>

            <h1 
              className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4" 
              style={{ fontFamily: 'Barlow Condensed, sans-serif', color: status.color }}
              data-testid="wellness-result-title"
            >
              {status.title}
            </h1>

            <div className="bg-black/60 border border-white/10 p-6 mb-6">
              <p className="text-lg text-white leading-relaxed" data-testid="wellness-result-message">
                {status.message}
              </p>
            </div>

            <div className="grid grid-cols-5 gap-2 mb-6">
              <div className="col-span-1 bg-[#0A0A0A] p-3 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">Score</p>
                <p className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: status.color }}>
                  {result.daily_score}
                </p>
              </div>
              <div className="col-span-1 bg-[#0A0A0A] p-2 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">Sensação</p>
                <p className="text-2xl font-bold text-[#00F0FF]">{result.como_se_sente}</p>
              </div>
              <div className="col-span-1 bg-[#0A0A0A] p-2 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">Dores</p>
                <p className="text-2xl font-bold text-[#00F0FF]">{result.dores_musculares}</p>
              </div>
              <div className="col-span-1 bg-[#0A0A0A] p-2 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">Recup.</p>
                <p className="text-2xl font-bold text-[#00F0FF]">{result.recuperacao}</p>
              </div>
              <div className="col-span-1 bg-[#0A0A0A] p-2 border border-white/5">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">Mental</p>
                <p className="text-2xl font-bold text-[#00F0FF]">{result.saude_mental}</p>
              </div>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-14 font-bold uppercase text-lg rounded-none"
              style={{ backgroundColor: status.color, color: '#000' }}
              data-testid="wellness-go-dashboard"
            >
              Ir para Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col relative">
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/79f43b2c808bf03b49df74617e42043ce286507138776852627882c75eb6dc21.png)',
          backgroundSize: 'cover'
        }}
      ></div>

      <div className="h-1 bg-[#121212] relative z-10">
        <div 
          className="h-full bg-[#00F0FF] transition-all duration-300"
          style={{ width: `${progress}%` }}
          data-testid="wellness-progress-bar"
        ></div>
      </div>

      <div className="absolute top-3 left-4 z-20">
        <NavArrows />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <div className="w-full max-w-2xl">
          <div className="flex flex-col items-center mb-12">
            <TCSLogo size="md" showTagline={false} />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mt-4">
              Questionário Diário de Bem-Estar
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 p-8 md:p-12" data-testid="wellness-question-card">
            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">
                Questão {currentQuestion + 1} de {questions.length}
              </span>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-10" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {question.title}
            </h2>

            {question.type === 'choice' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {question.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAnswers({ ...answers, [question.id]: opt.value })}
                    className={`p-6 border-2 text-left transition-all ${currentValue === opt.value ? 'bg-opacity-10' : 'border-white/10 bg-[#0A0A0A] hover:border-white/30'}`}
                    style={currentValue === opt.value ? { borderColor: opt.color, backgroundColor: `${opt.color}15` } : {}}
                    data-testid={`wellness-${question.id}-${opt.value}`}
                  >
                    <p className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: currentValue === opt.value ? opt.color : '#FFFFFF' }}>
                      {opt.label}
                    </p>
                    <p className="text-sm text-[#A1A1AA]">{opt.desc}</p>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-6 mb-8">
                  <button
                    onClick={handleDecrement}
                    disabled={currentValue <= 1}
                    className="w-16 h-16 md:w-20 md:h-20 bg-[#0A0A0A] border-2 border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                    data-testid={`wellness-${question.id}-minus`}
                    aria-label="Diminuir"
                  >
                    <Minus className="w-8 h-8" strokeWidth={3} />
                  </button>

                  <div className="bg-[#00F0FF]/10 border-2 border-[#00F0FF] w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
                    <span className="text-7xl md:text-8xl font-black text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid={`wellness-${question.id}-value`}>
                      {currentValue}
                    </span>
                  </div>

                  <button
                    onClick={handleIncrement}
                    disabled={currentValue >= 10}
                    className="w-16 h-16 md:w-20 md:h-20 bg-[#0A0A0A] border-2 border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                    data-testid={`wellness-${question.id}-plus`}
                    aria-label="Aumentar"
                  >
                    <Plus className="w-8 h-8" strokeWidth={3} />
                  </button>
                </div>

                <div className="flex justify-between mb-2 text-xs text-[#A1A1AA] uppercase tracking-wider">
                  <span>1 - {question.lowLabel}</span>
                  <span>10 - {question.highLabel}</span>
                </div>

                <div className="flex gap-1 mb-8">
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                    <button
                      key={n}
                      onClick={() => setAnswers({ ...answers, [question.id]: n })}
                      className={`flex-1 h-2 transition-all ${n <= currentValue ? 'bg-[#00F0FF]' : 'bg-[#0A0A0A] border border-white/10'}`}
                      data-testid={`wellness-${question.id}-dot-${n}`}
                      aria-label={`Selecionar ${n}`}
                    ></button>
                  ))}
                </div>
              </>
            )}

            <div className="flex gap-4">
              {currentQuestion > 0 && (
                <Button
                  onClick={handlePrevious}
                  variant="outline"
                  className="flex-1 bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none h-12 uppercase font-bold"
                  data-testid="wellness-previous-button"
                >
                  Anterior
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={loading || (question.type === 'choice' && answers[question.id] === 'normal')}
                className="flex-1 bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase h-12 rounded-none disabled:opacity-50"
                data-testid="wellness-next-button"
              >
                {currentQuestion === questions.length - 1 ? (loading ? 'Enviando...' : 'Finalizar') : 'Próxima'}
              </Button>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {questions.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-12 ${index <= currentQuestion ? 'bg-[#00F0FF]' : 'bg-[#121212]'} transition-colors`}
                data-testid={`wellness-indicator-${index}`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wellness;
